/* eslint-disable react/no-unescaped-entities */
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Scale, Shield, FileText, AlertTriangle, Lock, Users, RefreshCw } from 'lucide-react';

const LegalPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <Scale className="h-12 w-12 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-primary">Terms of Service</h1>
          <p className="text-slate-500 mt-2">Last updated: March 2026</p>
        </div>

        <div className="space-y-6">
          {/* Platform Disclaimer */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-5 w-5 text-blue-600" />
                Platform Disclaimer
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>
                PTC Client Hub is a client relationship management and financing application platform 
                operated by Prestigious Companies. This platform facilitates the submission and processing 
                of financing applications on behalf of clients.
              </p>
              <p>
                By using this platform, you acknowledge that you have read, understood, and agree to 
                be bound by these Terms of Service and our Privacy Policy.
              </p>
            </CardContent>
          </Card>

          {/* No Guarantee of Funding */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                No Guarantee of Funding
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>
                <strong>Prestigious Companies does not guarantee approval or funding of any financing application.</strong>
              </p>
              <p>
                All financing decisions are made solely by third-party lenders based on their own 
                underwriting criteria, credit policies, and risk assessments. Submission of an 
                application does not constitute approval or commitment of funds.
              </p>
              <p>
                Interest rates, terms, and conditions are determined by the lender and may vary 
                based on creditworthiness, loan amount, and other factors.
              </p>
            </CardContent>
          </Card>

          {/* Third-Party Lender Disclaimer */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5 text-purple-600" />
                Third-Party Lender Disclaimer
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>
                Prestigious Companies acts as an intermediary between clients and third-party lending 
                institutions. We are not a lender and do not make credit decisions.
              </p>
              <p>
                The lenders participating in our network are independent entities with their own 
                terms, conditions, and privacy practices. We encourage you to review each lender's 
                terms before accepting any financing offer.
              </p>
              <p>
                We do not endorse, guarantee, or assume responsibility for any lender's products, 
                services, or actions.
              </p>
            </CardContent>
          </Card>

          {/* Data Usage Notice */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className="h-5 w-5 text-green-600" />
                Data Usage Notice
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>
                By submitting information through this platform, you consent to the collection, 
                storage, and sharing of your data as described in our Privacy Policy.
              </p>
              <p>
                Client information may be shared with:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Third-party lenders for the purpose of processing financing applications</li>
                <li>Service providers who assist in platform operations</li>
                <li>Regulatory authorities as required by law</li>
              </ul>
            </CardContent>
          </Card>

          {/* Limitation of Liability */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lock className="h-5 w-5 text-red-600" />
                Limitation of Liability
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>
                To the maximum extent permitted by law, Prestigious Companies shall not be liable 
                for any indirect, incidental, special, consequential, or punitive damages arising 
                from your use of this platform.
              </p>
              <p>
                This includes, but is not limited to:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Denial of financing applications</li>
                <li>Delays in processing</li>
                <li>Loss of business opportunities</li>
                <li>Errors or omissions in lender communications</li>
                <li>Technical issues or service interruptions</li>
              </ul>
            </CardContent>
          </Card>

          {/* Unauthorized Use */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Unauthorized Use
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>
                You agree not to:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Access or attempt to access accounts or data belonging to others</li>
                <li>Submit false, misleading, or fraudulent information</li>
                <li>Use the platform for any illegal or unauthorized purpose</li>
                <li>Interfere with or disrupt the platform's security features</li>
                <li>Attempt to reverse engineer or extract source code from the platform</li>
                <li>Share login credentials or allow unauthorized access to your account</li>
              </ul>
              <p className="mt-3">
                Violation of these terms may result in immediate termination of access and may 
                be reported to appropriate authorities.
              </p>
            </CardContent>
          </Card>

          {/* Terms Update Clause */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <RefreshCw className="h-5 w-5 text-blue-600" />
                Updates to Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>
                Prestigious Companies reserves the right to modify these Terms of Service at any 
                time. Changes will be effective immediately upon posting to the platform.
              </p>
              <p>
                Continued use of the platform after changes are posted constitutes acceptance 
                of the revised terms. We encourage you to review these terms periodically.
              </p>
              <p>
                For significant changes, we will make reasonable efforts to notify users via 
                email or platform notification.
              </p>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="py-6 text-center">
              <p className="text-slate-600">
                For questions about these Terms of Service, please contact:
              </p>
              <p className="font-semibold text-primary mt-2">
                support@prestigiouscompanies.org
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-8 text-sm text-slate-400">
          © 2026 Prestigious Companies. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
